import { styled, useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Check, ChevronRight } from "baseui/icon";
import { ListItem, ListItemLabel } from "baseui/list";
import { HeadingMedium, HeadingXSmall, LabelSmall, ParagraphSmall, ParagraphXSmall } from "baseui/typography";
import { useEffect, useState } from "react";
import { VscClose, VscEdit, VscLink, VscOrganization, VscSymbolClass, VscTrash } from "react-icons/vsc";
import { useGetResourceGroupByIdQuery } from "../../services/resourceGroups/resourceGroupApi";
import {
  useGetTenantByIdQuery,
  useGetTenantsResourceGroupQuery,
  useRemoveResourceGroupMutation,
  useRemoveUserFromTenantMutation,
} from "../../services/resourceGroups/tenantApi";
import { useSnackbar, DURATION } from "baseui/snackbar";
import { LoadingView } from "../../components/LoadingView";
import { useGetUsersQuery, useUpdateUserMutation } from "../../services/users/userApi";
import LinkResourceGroupDialog from "./linkResourceGroupDialog/LinkResourceGroupDialog";
import { ColoredAvatar } from "../../components/ColoredAvatar/ColoredAvatar";
import { Spinner } from "baseui/spinner";
import { HorizontalStackPanel } from "../../components/Layout/StackPanel";
import TenantAddress from "./TenantAddress";
import { DeleteTenantDialog } from ".";
import { InviteUserDialog } from "../user";
import { Badge } from "baseui/badge";
import UpdateDialog from "./updateDialog/UpdateDialog";
import { Avatar } from "baseui/avatar";
import { useReportMutationState } from "../../hooks/useReportMutationState";
import { FormattedMessage } from "react-intl";
import { IPerson } from "@oqdo/automationapps/Agents/Person";
import { IResourceGroup } from "@oqdo/automationapps/Memberships/ResourceGroup";

interface TenantProps {
  tenantId: string;
  subscriptionId: string;
}
const Tenant = ({ tenantId, subscriptionId }: TenantProps) => {
  const [css, theme] = useStyletron();
  const { data: tenant, isLoading, isSuccess, error } = useGetTenantByIdQuery(tenantId);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenUser, setIsOpenUser] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);

  return (
    <Block width="100%" className={css({ position: "relative" })}>
      <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
        <LabelSmall color={theme.colors.accent}>
          <FormattedMessage id="Tenant" defaultMessage={"Tenant"} />
        </LabelSmall>
        <HorizontalStackPanel>
          {tenant?.logo ? (
            <Avatar
              name={tenant.name}
              size="scale800"
              overrides={{
                Avatar: {
                  style: ({ $theme }) => ({
                    height: "auto",
                  }),
                },
              }}
              src={tenant?.logo}
            />
          ) : (
            <VscOrganization color={theme.colors.primary} />
          )}
          <HeadingMedium marginTop={0} marginBottom={0}>
            {tenant?.name}
          </HeadingMedium>
        </HorizontalStackPanel>
        <ParagraphXSmall marginTop={0}>{tenantId}</ParagraphXSmall>

        <>
          <div
            className={css({
              position: "absolute",
              top: "-" + theme.sizing.scale800,
              right: theme.sizing.scale500,
            })}>
            <Button
              onClick={() => {
                setIsOpenUpdate(true);
              }}
              title="Edit"
              size="default"
              kind="secondary"
              shape="circle">
              <VscEdit />
            </Button>
            <Button
              shape="circle"
              kind="secondary"
              disabled={!tenant}
              onClick={() => {
                setIsOpenDelete(true);
              }}>
              <VscTrash />
            </Button>
          </div>
          {isOpenUpdate && tenant && <UpdateDialog tenant={tenant} isOpen={isOpenUpdate} setIsOpen={setIsOpenUpdate} />}
          {isOpenDelete && tenant && (
            <DeleteTenantDialog
              subscriptionId={subscriptionId}
              isOpen={isOpenDelete}
              setIsOpen={setIsOpenDelete}
              tenant={tenant}
            />
          )}
        </>

        {tenant?.address && <TenantAddress address={tenant?.address} />}
        <HeaderRow>
          <HeadingXSmall marginBottom={0}>
            <FormattedMessage id="resourceGroups" defaultMessage={"Resource Groups"} />
          </HeadingXSmall>
          {tenant && (
            <>
              <Button
                onClick={() => {
                  setIsOpen(true);
                }}
                title="Link Resource Group"
                size="compact"
                kind="primary"
                shape="pill">
                <VscLink />
              </Button>
              <LinkResourceGroupDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                subscriptionId={subscriptionId}
                tenantId={tenantId}
              />
            </>
          )}
        </HeaderRow>
        <ResourceGroups subscriptionId={subscriptionId} tenantId={tenantId} />

        <HeaderRow>
          <HeadingXSmall marginBottom={0}>
            <FormattedMessage id="users" defaultMessage="Users" />
          </HeadingXSmall>
          {tenant && (
            <>
              <Button
                onClick={() => {
                  setIsOpenUser(true);
                }}
                title="Invite User"
                size="compact"
                kind="primary"
                shape="pill">
                <FormattedMessage id="add" defaultMessage="Add" />
              </Button>
              {isOpenUser && <InviteUserDialog tenantId={tenantId} isOpen={isOpenUser} setIsOpen={setIsOpenUser} />}
            </>
          )}
        </HeaderRow>
        <Users subscriptionId={subscriptionId} tenantId={tenantId} />
      </LoadingView>
    </Block>
  );
};

export default Tenant;

const Users = (props: { tenantId: string; subscriptionId: string }) => {
  const { tenantId, subscriptionId } = props;
  const [css, theme] = useStyletron();

  const { data, isLoading, isSuccess, error } = useGetUsersQuery({
    tenantId: tenantId,
  });

  return (
    <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
      <ul
        className={css({
          paddingLeft: 0,
          paddingRight: 0,
          overflowY: "auto",
          maxHeight: "30vh",
        })}>
        {data?.map((user: IPerson, index) => (
          <User key={user.id} subscriptionId={subscriptionId} user={user} tenantId={tenantId} />
        ))}
      </ul>
    </LoadingView>
  );
};

const User = (props: { user: IPerson; tenantId: string; subscriptionId: string }) => {
  const { user, subscriptionId } = props;
  const [removeUserFromTenant, removeUserFromTenantResult] = useRemoveUserFromTenantMutation();
  useReportMutationState(removeUserFromTenantResult);
  const [update, updateResult] = useUpdateUserMutation();
  useReportMutationState(update);

  const onRemove = async () => await removeUserFromTenant({ tenantId: props.tenantId, userId: user.id });

  const disabled = user.accountEnabled !== undefined && user.accountEnabled !== null ? !user.accountEnabled : false;
  const isNotActive = user.pendingInvitation;

  const handleDisable = async () => await update({ id: user.id, patch: { accountEnabled: false } });
  const handleEnable = async () => await update({ id: user.id, patch: { accountEnabled: true } });
  return (
    <ListItem
      shape="ROUND"
      overrides={{
        Root: {
          style: ({ $theme }) => ({
            backgroundColor: disabled ? $theme.colors.backgroundSecondary : undefined,
            // opacity: disabled ? 0.5 : undefined,
            filter: disabled ? "grayscale(80%)" : undefined,
          }),
        },
      }}
      artwork={() => <ColoredAvatar username={user.displayName} />}
      key={user.id}
      endEnhancer={() => (
        <HorizontalStackPanel $alignItems={"center"}>
          {disabled && (
            <Button
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    color: $theme.colors.accent,
                  }),
                },
              }}
              shape="pill"
              kind="secondary"
              size="compact"
              onClick={handleEnable}
              isLoading={updateResult.isLoading}>
              <FormattedMessage id="Enable" defaultMessage="Enable" />
            </Button>
          )}
          {!disabled && (
            <Button
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    color: $theme.colors.negative,
                  }),
                },
              }}
              shape="pill"
              kind="secondary"
              size="compact"
              onClick={handleDisable}
              isLoading={updateResult.isLoading}>
              <FormattedMessage id="Disable" defaultMessage="Disable" />
            </Button>
          )}
          {isNotActive && (
            <>
              {" "}
              <ParagraphXSmall margin={0}>{"Invitation Status"}</ParagraphXSmall>{" "}
              <Badge
                hierarchy={"secondary"}
                color={"warning"}
                content={user.pendingInvitation ? "Pending Invitation" : ""}
              />
            </>
          )}
          <Button
            onClick={onRemove}
            isLoading={removeUserFromTenantResult.isLoading}
            title="Remove User from tenant"
            size="compact"
            kind="secondary"
            shape="pill">
            <VscTrash />
          </Button>
          <Button $as="a" href={`/subscriptions/${subscriptionId}/users/${user.id}`} kind="tertiary" shape="circle">
            <ChevronRight size="scale800" />
          </Button>
        </HorizontalStackPanel>
      )}>
      <ListItemLabel description={user.mail}>
        {user.displayName}{" "}
        {disabled && (
          <Badge
            content={<FormattedMessage id="AccountDisabled" defaultMessage="Account disabled" />}
            hierarchy="secondary"
            color="negative"
          />
        )}{" "}
      </ListItemLabel>
    </ListItem>
  );
};

const ResourceGroups = (props: { tenantId: string; subscriptionId: string }) => {
  const { tenantId, subscriptionId } = props;
  const [css, theme] = useStyletron();
  const { data: allowedResourceGroups, isLoading } = useGetTenantsResourceGroupQuery(tenantId);
  if (isLoading) return <Spinner $size={"10px"} />;

  const hasRgs = allowedResourceGroups && allowedResourceGroups.length > 0;
  if (!hasRgs)
    return <ParagraphSmall className={css({ color: theme.colors.primary400 })}>no resource groups</ParagraphSmall>;
  return (
    <ul
      className={css({
        paddingLeft: 0,
        paddingRight: 0,
        overflowY: "auto",
        maxHeight: "30vh",
      })}>
      {allowedResourceGroups?.map((rg: IResourceGroup, index) => (
        <ResourceGroup tenantId={tenantId} key={rg.id} resourceGroupId={rg.id} subscriptionId={subscriptionId} />
      ))}
    </ul>
  );
};

const ResourceGroup = (props: { resourceGroupId: string; tenantId: string; subscriptionId: string }) => {
  const { resourceGroupId, tenantId, subscriptionId } = props;
  const [remove, removeResult] = useRemoveResourceGroupMutation();
  const { enqueue } = useSnackbar();
  const { data: rg } = useGetResourceGroupByIdQuery(resourceGroupId);

  function onRemove() {
    remove({ resourceGroupId: rg?.id || "", tenantId: tenantId });
  }

  useEffect(() => {
    if (removeResult.isSuccess) {
      enqueue(
        {
          message: "ResourceGroup Successfully removed",
          startEnhancer: ({ size }) => <Check size={size} />,
        },
        DURATION.medium
      );
    }
  }, [removeResult.isSuccess]);
  return (
    <>
      {rg && (
        <ListItem
          artwork={() => <VscSymbolClass />}
          key={rg.id}
          endEnhancer={() => (
            <>
              <Button
                onClick={onRemove}
                title="Remove Resource Group from tenant"
                size="compact"
                kind="secondary"
                shape="pill">
                <VscClose />
              </Button>
              <Button
                $as="a"
                href={`/subscriptions/${subscriptionId}/resourceGroups/${rg.id}`}
                kind="tertiary"
                shape="circle">
                <ChevronRight size="scale800" />
              </Button>
            </>
          )}>
          <ListItemLabel>
            {rg.name} {rg.disabled && <Badge content="DISABLED" color="primary" />}
          </ListItemLabel>
        </ListItem>
      )}
    </>
  );
};

const HeaderRow = styled<"div", {}>("div", ({ $theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
}));
